import { useState } from 'react';

export const SOLID_COLORS = [
  '#a855f7', '#ec4899', '#3b82f6', '#06b6d4', '#22c55e',
  '#14b8a6', '#f97316', '#ef4444', '#f59e0b', '#eab308',
  '#f43f5e', '#6366f1', '#8b5cf6', '#d946ef', '#0ea5e9',
  '#10b981', '#84cc16', '#64748b', '#78716c', '#ffffff',
];

export const GRADIENT_PRESETS = [
  { label: 'Purple-Pink', from: '#a855f7', to: '#ec4899' },
  { label: 'Blue-Cyan', from: '#3b82f6', to: '#06b6d4' },
  { label: 'Green-Teal', from: '#22c55e', to: '#14b8a6' },
  { label: 'Orange-Red', from: '#f97316', to: '#ef4444' },
  { label: 'Amber-Yellow', from: '#f59e0b', to: '#eab308' },
  { label: 'Rose-Purple', from: '#f43f5e', to: '#a855f7' },
  { label: 'Indigo-Blue', from: '#6366f1', to: '#3b82f6' },
  { label: 'Emerald-Lime', from: '#10b981', to: '#84cc16' },
  { label: 'Fuchsia-Violet', from: '#d946ef', to: '#8b5cf6' },
  { label: 'Sky-Indigo', from: '#0ea5e9', to: '#6366f1' },
];

interface ColorPickerProps {
  colorType: 'solid' | 'gradient';
  color: string;
  gradientFrom: string;
  gradientTo: string;
  onColorTypeChange: (type: 'solid' | 'gradient') => void;
  onColorChange: (color: string) => void;
  onGradientChange: (from: string, to: string) => void;
}

export default function ColorPicker({
  colorType, color, gradientFrom, gradientTo,
  onColorTypeChange, onColorChange, onGradientChange,
}: ColorPickerProps) {
  return (
    <div className="space-y-3">
      {/* Type Toggle */}
      <div className="flex gap-1.5">
        {(['solid', 'gradient'] as const).map(type => (
          <button
            key={type}
            type="button"
            onClick={() => onColorTypeChange(type)}
            className={`px-2.5 py-1 text-[10px] rounded-md font-medium capitalize transition-colors ${
              colorType === type
                ? 'bg-purple-600 text-white'
                : 'bg-dark-700 text-gray-400 hover:text-white border border-white/10'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Solid Controls */}
      {colorType === 'solid' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 w-12">Color</span>
            <input
              type="color"
              value={color || '#64748b'}
              onChange={(e) => onColorChange(e.target.value)}
              className="w-7 h-7 rounded cursor-pointer border border-white/10 bg-transparent"
            />
            <div
              className="w-8 h-5 rounded-md ml-1"
              style={{ backgroundColor: color || '#64748b' }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SOLID_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => onColorChange(c)}
                className={`w-6 h-6 rounded-md border transition-colors ${
                  color === c ? 'border-white ring-1 ring-white/30' : 'border-white/10 hover:border-white/30'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gradient Controls */}
      {colorType === 'gradient' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 w-12">From</span>
            <input
              type="color"
              value={gradientFrom || '#a855f7'}
              onChange={(e) => onGradientChange(e.target.value, gradientTo)}
              className="w-7 h-7 rounded cursor-pointer border border-white/10 bg-transparent"
            />
            <span className="text-[10px] text-gray-500 ml-3 w-12">To</span>
            <input
              type="color"
              value={gradientTo || '#ec4899'}
              onChange={(e) => onGradientChange(gradientFrom, e.target.value)}
              className="w-7 h-7 rounded cursor-pointer border border-white/10 bg-transparent"
            />
            <div
              className="flex-1 h-5 rounded-md ml-2"
              style={{ background: `linear-gradient(90deg, ${gradientFrom || '#a855f7'}, ${gradientTo || '#ec4899'})` }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GRADIENT_PRESETS.map(preset => (
              <button
                key={preset.label}
                type="button"
                onClick={() => onGradientChange(preset.from, preset.to)}
                title={preset.label}
                className="w-10 h-5 rounded-md border border-white/10 hover:border-white/30 transition-colors"
                style={{ background: `linear-gradient(90deg, ${preset.from}, ${preset.to})` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
